import {
  ChannelTitleUpdateInput,
  OnlineTravelAgency,
} from '@avantstay/arriere-clients/dist/arriereBackoffice'

import {
  DistributionChannelsSubSection,
  HomeListingView,
  ListingDescriptionFormFields,
} from '@new/domains/properties/screens/propertyDistributionChannels/DistributionChannels.types'

// Channels that support a per-channel Listing Name override on Rentals United.
// Each entry maps the OTA enum to the friendly form-field name used in the UI.
export const RU_CHANNEL_TITLE_OTAS: {
  ota: OnlineTravelAgency
  formField: 'bookingComTitle' | 'expediaTitle' | 'marriottTitle'
}[] = [
  { ota: OnlineTravelAgency.bookingCom, formField: 'bookingComTitle' },
  { ota: OnlineTravelAgency.expedia, formField: 'expediaTitle' },
  { ota: OnlineTravelAgency.marriott, formField: 'marriottTitle' },
]

// Reads per-channel override titles from the channels array (each entry's
// `title` carries the override; absent/empty means no override is set).
export const flattenChannelTitles = (
  channels?: HomeListingView['channels'],
): Pick<ListingDescriptionFormFields, 'bookingComTitle' | 'expediaTitle' | 'marriottTitle'> => {
  const result: Record<string, string | undefined> = {}
  RU_CHANNEL_TITLE_OTAS.forEach(({ ota, formField }) => {
    const match = channels?.find(channel => channel.ota === ota)
    result[formField] = match?.title || undefined
  })
  return result
}

// Builds the mutation payload from the UI form state. Always emits an entry per
// supported channel: a trimmed value upserts the override, an empty value omits
// `title` (equivalent to `None` server-side) to delete the existing override.
export const buildChannelTitlesPayload = (
  formFields: ListingDescriptionFormFields,
): ChannelTitleUpdateInput[] =>
  RU_CHANNEL_TITLE_OTAS.map(({ ota, formField }) => {
    const trimmed = formFields[formField]?.trim()
    return trimmed ? { ota, title: trimmed } : { ota }
  })

export const getChannelTitle = (
  homeListing: HomeListingView | undefined,
  ota: OnlineTravelAgency,
): string | undefined =>
  homeListing?.channels?.find(channel => channel.ota === ota)?.title || undefined

const listingDescriptionFields = {
  title: {
    title: 'Listing Name',
    beforeSection: 'Primary Listing',
  },
  description: {
    title: 'Description',
  },
  propertyType: {
    title: 'Property Type',
  },
  bookingComTitle: {
    title: 'Booking.com Listing Name',
    beforeSection: 'Channel-specific Listing Names',
    helperText: 'Leave blank to fall back to the Listing Name. Max 150 characters per channel.',
  },
  expediaTitle: {
    title: 'Expedia Listing Name',
  },
  marriottTitle: {
    title: 'Marriott Listing Name',
  },
}

export const rentalsUnitedListingDataGroups = [
  {
    title: 'Listing Description',
    dataFields: listingDescriptionFields,
    sectionKey: DistributionChannelsSubSection.listingDescription,
  },
]
